import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import { fmtDateGlobal } from '../lib/dateFormat';

const THRESHOLD_OPTIONS = [30, 60, 90, 120, 180, 365];

const DeadStock = ({ user }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    api.get('/brands').then(r => setBrands(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('days', days);
      if (filterBrands.length) params.append('brand_id', filterBrands.join(','));
      const res = await api.get(`/dead-stock?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load dead stock data');
    } finally {
      setLoading(false);
    }
  }, [days, filterBrands]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getAgingBadge = (daysSince) => {
    if (daysSince >= 365) return { label: '1Y+', cls: 'bg-red-600 text-white' };
    if (daysSince >= 180) return { label: '6M+', cls: 'bg-red-100 text-red-700' };
    if (daysSince >= 120) return { label: '4M+', cls: 'bg-orange-100 text-orange-700' };
    if (daysSince >= 90) return { label: '3M+', cls: 'bg-amber-100 text-amber-700' };
    if (daysSince >= 60) return { label: '2M+', cls: 'bg-yellow-100 text-yellow-700' };
    return { label: `${daysSince}d`, cls: 'bg-gray-100 text-gray-600' };
  };

  const brandOpts = brands.map(b => ({ value: b.id, label: b.name }));

  return (
    <div className="space-y-4 p-6" data-testid="dead-stock-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="dead-stock-title">
            Dead Stock Report
          </h1>
          <p className="text-sm text-gray-500">Designs with no sales beyond the selected threshold</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="dead-stock-filters"><CardContent className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <p className="text-xs text-gray-500 mb-1.5 font-medium">Days Without Sale</p>
            <div className="flex gap-1">
              {THRESHOLD_OPTIONS.map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${days === d
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  data-testid={`threshold-${d}`}>
                  {d >= 365 ? '1Y' : `${d}d`}
                </button>
              ))}
            </div>
          </div>
          <div className="min-w-[180px]">
            <p className="text-xs text-gray-500 mb-1.5 font-medium">Brand</p>
            <MultiSelectFilter label="All Brands" options={brandOpts} selected={filterBrands} onChange={setFilterBrands} />
          </div>
          {filterBrands.length > 0 && (
            <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={() => setFilterBrands([])}>Clear</Button>
          )}
        </div>
      </CardContent></Card>

      {/* Summary Cards */}
      {data && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dead-stock-summary">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 font-medium">Dead Designs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_designs}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">No sales in {data.days_threshold}+ days</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 font-medium">Total Dead Stock</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{data.total_dead_stock_qty.toLocaleString('en-IN')} pcs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 font-medium">Stock Value (WSP)</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">
                {data.total_dead_stock_value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 font-medium">Threshold</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.days_threshold} days</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Since last sale</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading...</CardContent></Card>
      ) : !data || data.rows.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400" data-testid="no-dead-stock">
          No dead stock found for {days}+ days threshold
        </CardContent></Card>
      ) : (
        <Card data-testid="dead-stock-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Design</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Brand</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Category</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Stock</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Value (WSP)</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Last Sale</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Aging</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Total Sold</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Added On</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const aging = getAgingBadge(r.days_since_sale);
                    const isExpanded = expandedRow === i;
                    return (
                      <React.Fragment key={r.design_code}>
                        <tr className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                          onClick={() => setExpandedRow(isExpanded ? null : i)} data-testid={`dead-stock-row-${i}`}>
                          <td className="px-4 py-2.5 font-bold text-gray-900">{r.design_code}</td>
                          <td className="px-4 py-2.5 text-gray-600">{r.brand}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{r.category}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-red-600">{r.total_stock}</td>
                          <td className="px-4 py-2.5 text-right font-medium text-gray-800">
                            {r.stock_value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">
                            {r.last_sale_date ? fmtDateGlobal(r.last_sale_date) : <span className="text-red-500 font-medium">Never</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge className={`${aging.cls} text-[10px]`}>{aging.label}</Badge>
                            <span className="text-[10px] text-gray-400 ml-1">{r.days_since_sale}d</span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{r.total_sold}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{fmtDateGlobal(r.date_added)}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-blue-50 border-b">
                            <td colSpan={9} className="px-4 py-3">
                              <div className="flex flex-wrap gap-2">
                                <span className="text-xs text-gray-500 font-medium mr-2">Size-wise Stock:</span>
                                {Object.entries(r.size_stock || {}).filter(([, qty]) => qty > 0).map(([size, qty]) => (
                                  <span key={size} className="text-xs bg-white border rounded px-2 py-0.5">
                                    <span className="font-medium text-gray-700">{size}</span>: <span className="text-red-600 font-bold">{qty}</span>
                                  </span>
                                ))}
                                {r.last_purchase_date && (
                                  <span className="text-xs text-gray-400 ml-4">Last Purchase: {fmtDateGlobal(r.last_purchase_date)}</span>
                                )}
                                <span className="text-xs text-gray-400 ml-4">WSP: {r.avg_wsp}</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DeadStock;
